"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  destructive?: boolean;
  loading?: boolean;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  contentClassName?: string;
  /** 対象・金額など、実行前に確認する構造化された内容。 */
  children?: ReactNode;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "削除する",
  cancelLabel = "キャンセル",
  onConfirm,
  destructive = true,
  loading = false,
  confirmDisabled = false,
  cancelDisabled = false,
  contentClassName,
  children,
}: ConfirmDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!loading && !cancelDisabled) onOpenChange(nextOpen);
      }}
    >
      <AlertDialogContent
        className={cn(
          children &&
            "flex max-h-[92dvh] w-[calc(100vw-2rem)] max-w-2xl flex-col rounded-lg",
          contentClassName
        )}
        aria-busy={loading || undefined}
        onEscapeKeyDown={(event) => {
          if (loading || cancelDisabled) event.preventDefault();
        }}
      >
        <AlertDialogHeader className="shrink-0">
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {/* 変更前後を並べて確認させたい呼び出し元があるため、改行を潰さない */}
          <AlertDialogDescription className="whitespace-pre-line">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {children && (
          <div className="min-h-0 overflow-y-auto overscroll-contain">
            {children}
          </div>
        )}
        <AlertDialogFooter className="shrink-0">
          <AlertDialogCancel disabled={loading || cancelDisabled}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={loading || confirmDisabled}
            className={
              destructive
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : undefined
            }
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
